<template>
  <div>
    <div class="logic-info-box" :class="{ collapsed: !showLogicInfo }">
      <div class="info-header" @click="showLogicInfo = !showLogicInfo">
        <div class="info-header-left">
          <i class="ri-information-line"></i>
          <span class="info-title">條件邏輯說明</span>
        </div>
        <i class="expand-icon" :class="showLogicInfo ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"></i>
      </div>
      <transition name="slide-fade">
        <div v-show="showLogicInfo" class="info-content">
          <ul class="info-list">
            <li><strong>條件組內</strong>：可選擇 <span class="badge badge-and">AND</span> 或 <span class="badge badge-or">OR</span>
              <ul>
                <li><span class="badge badge-and">AND</span>：所有條件都必須符合</li>
                <li><span class="badge badge-or">OR</span>：任一條件符合即可</li>
              </ul>
            </li>
            <li><strong>條件組之間</strong>：固定為 <span class="badge badge-or">OR</span> 關係
              <ul>
                <li>只要會員符合<strong>任一條件組</strong>，{{ mode === 'audience' ? '就會被加入受眾' : '就會被貼上標籤' }}</li>
              </ul>
            </li>
          </ul>
          <p class="info-example">
            <strong>範例：</strong><br>
            條件組 1：(註冊天數 ≤ 30 <span class="badge badge-and">AND</span> 總消費次數 ≥ 1)<br>
            條件組 2：(會員等級 = 金卡 <span class="badge badge-and">AND</span> 最後消費天數 ≤ 7)<br>
            <span class="result-text">→ 符合條件組 1 <span class="badge badge-or">OR</span> 條件組 2 的會員都會{{ mode === 'audience' ? '被加入受眾' : '被標記' }}</span>
          </p>
        </div>
      </transition>
    </div>

    <div v-for="(group, groupIndex) in modelValue" :key="groupIndex" class="condition-group">
      <div class="group-header">
        <span>條件組 {{ groupIndex + 1 }}</span>
        <button
          v-if="modelValue.length > 1"
          type="button"
          class="btn btn-sm btn-danger"
          @click="removeConditionGroup(groupIndex)"
        >
          刪除條件組
        </button>
      </div>

      <div class="form-group">
        <label>邏輯運算子</label>
        <div class="radio-group">
          <label class="radio-label">
            <input type="radio" v-model="group.logicalOperator" value="AND" />
            AND（所有條件都符合）
          </label>
          <label class="radio-label">
            <input type="radio" v-model="group.logicalOperator" value="OR" />
            OR（任一條件符合）
          </label>
        </div>
      </div>

      <div v-for="(condition, conditionIndex) in group.conditions" :key="condition.id || conditionIndex" class="condition-item">
        <div class="condition-row">
          <div class="form-group flex-1">
            <select v-model="condition.conditionType" class="form-control" @change="handleConditionTypeChange(condition)">
              <option value="">選擇條件類型</option>
              <option value="registration_days">註冊天數</option>
              <option v-if="mode === 'audience'" value="registration_date">註冊日期</option>
              <option value="total_purchase_count">總消費次數</option>
              <option value="total_purchase_amount">總消費金額</option>
              <option v-if="mode === 'audience'" value="available_points">點數</option>
              <option value="last_purchase_days">最後消費天數</option>
              <option value="purchase_count_within_days">時間內交易次數</option>
              <option value="purchase_amount_within_days">時間內交易金額</option>
              <option value="member_tier">會員等級</option>
              <option value="age">年齡</option>
              <option value="gender">性別</option>
              <option value="birth_month">生日月份</option>
              <option v-if="mode === 'audience'" value="location">居住地區</option>
              <option value="tag_exists">標籤</option>
              <option value="purchased_category">購買品類</option>
              <option value="purchased_product">購買商品</option>
              <option value="purchased_product_name">購買品名關鍵字</option>
            </select>
          </div>
          <div class="form-group flex-1">
            <select v-model="condition.operator" class="form-control" @change="handleOperatorChange(condition)">
              <option value="">選擇運算子</option>
              <template v-if="condition.conditionType === 'tag_exists'">
                <option value="exists">存在（擁有此標籤）</option>
                <option value="not_exists">不存在（沒有此標籤）</option>
              </template>
              <template v-else-if="purchaseTypes.includes(condition.conditionType)">
                <option value="greater_or_equal">達到購買次數門檻</option>
              </template>
              <template v-else-if="condition.conditionType === 'registration_date'">
                <option value="greater_or_equal">此日期之後（含）</option>
                <option value="less_or_equal">此日期之前（含）</option>
                <option value="between">介於之間</option>
              </template>
              <template v-else-if="plainNumericTypes.includes(condition.conditionType) || periodTypes.includes(condition.conditionType)">
                <option value="equals">等於</option>
                <option value="not_equals">不等於</option>
                <option value="greater_than">大於</option>
                <option value="greater_or_equal">大於等於</option>
                <option value="less_than">小於</option>
                <option value="less_or_equal">小於等於</option>
                <option value="between">介於之間</option>
              </template>
              <template v-else-if="choiceTypes.includes(condition.conditionType)">
                <option value="equals">等於</option>
                <option value="not_equals">不等於</option>
                <option value="in">在範圍內</option>
                <option value="not_in">不在範圍內</option>
              </template>
            </select>
          </div>
          <div class="form-group flex-2">
            <select
              v-if="condition.conditionType === 'member_tier' && !isMultiChoice(condition)"
              v-model="condition.value"
              class="form-control"
            >
              <option value="">請選擇會員等級</option>
              <option v-for="tier in memberTiers" :key="tier.value" :value="tier.value">{{ tier.label }}</option>
            </select>
            <div v-else-if="condition.conditionType === 'member_tier'" class="checkbox-group">
              <label v-for="tier in memberTiers" :key="tier.value" class="checkbox-label">
                <input
                  type="checkbox"
                  :checked="isChoiceChecked(condition, tier.value)"
                  @change="toggleChoice(condition, tier.value)"
                />
                {{ tier.label }}
              </label>
            </div>

            <select
              v-else-if="condition.conditionType === 'gender' && !isMultiChoice(condition)"
              v-model="condition.value"
              class="form-control"
            >
              <option value="">請選擇性別</option>
              <option v-for="option in genderOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
            </select>
            <div v-else-if="condition.conditionType === 'gender'" class="checkbox-group">
              <label v-for="option in genderOptions" :key="option.value" class="checkbox-label">
                <input
                  type="checkbox"
                  :checked="isChoiceChecked(condition, option.value)"
                  @change="toggleChoice(condition, option.value)"
                />
                {{ option.label }}
              </label>
            </div>

            <select
              v-else-if="condition.conditionType === 'birth_month' && !isMultiChoice(condition)"
              v-model="condition.value"
              class="form-control"
            >
              <option value="">請選擇月份</option>
              <option v-for="option in birthMonthOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
            </select>
            <div v-else-if="condition.conditionType === 'birth_month'" class="checkbox-group">
              <label v-for="option in birthMonthOptions" :key="option.value" class="checkbox-label">
                <input
                  type="checkbox"
                  :checked="isChoiceChecked(condition, option.value)"
                  @change="toggleChoice(condition, option.value)"
                />
                {{ option.label }}
              </label>
            </div>

            <select
              v-else-if="condition.conditionType === 'location' && !isMultiChoice(condition)"
              v-model="condition.value"
              class="form-control"
            >
              <option value="">請選擇縣市</option>
              <option v-for="city in cities" :key="city" :value="city">{{ city }}</option>
            </select>
            <div v-else-if="condition.conditionType === 'location'" class="checkbox-group">
              <label v-for="city in cities" :key="city" class="checkbox-label">
                <input
                  type="checkbox"
                  :checked="isChoiceChecked(condition, city)"
                  @change="toggleChoice(condition, city)"
                />
                {{ city }}
              </label>
            </div>

            <TagSelector
              v-else-if="condition.conditionType === 'tag_exists'"
              v-model="condition.value"
              placeholder="請選擇標籤"
              :allow-create="false"
            />

            <div v-else-if="condition.conditionType === 'purchased_category'" class="purchase-condition">
              <select v-model="condition.value.categoryId" class="form-control">
                <option value="">請選擇品類</option>
                <option v-for="cat in categoryOptions" :key="cat.id" :value="cat.id">{{ cat.label }}</option>
              </select>
              <div class="purchase-params">
                <div class="purchase-param">
                  <span class="param-label">買過 ≥</span>
                  <input v-model.number="condition.value.minCount" type="number" min="1" class="form-control param-input" placeholder="1" />
                  <span class="param-label">次</span>
                </div>
                <div class="purchase-param">
                  <span class="param-label">近</span>
                  <input v-model.number="condition.value.withinDays" type="number" min="1" class="form-control param-input" placeholder="不限" />
                  <span class="param-label">天內</span>
                </div>
              </div>
              <small class="form-text">次數以已完成訂單數計；時間範圍留空表示不限時間</small>
            </div>

            <div v-else-if="condition.conditionType === 'purchased_product'" class="purchase-condition">
              <button type="button" class="btn btn-secondary product-pick-btn" @click="openProductPicker(condition)">
                <i class="ri-shopping-bag-3-line"></i>
                {{ condition.value.productName || '選擇商品' }}
              </button>
              <div class="purchase-params">
                <div class="purchase-param">
                  <span class="param-label">買過 ≥</span>
                  <input v-model.number="condition.value.minCount" type="number" min="1" class="form-control param-input" placeholder="1" />
                  <span class="param-label">次</span>
                </div>
                <div class="purchase-param">
                  <span class="param-label">近</span>
                  <input v-model.number="condition.value.withinDays" type="number" min="1" class="form-control param-input" placeholder="不限" />
                  <span class="param-label">天內</span>
                </div>
              </div>
              <small class="form-text">次數以已完成訂單數計；時間範圍留空表示不限時間</small>
            </div>

            <div v-else-if="condition.conditionType === 'purchased_product_name'" class="purchase-condition">
              <input
                v-model="condition.value.keywordsText"
                type="text"
                class="form-control"
                placeholder="輸入品名關鍵字，多個以逗號分隔（例：咔啦, 辣腿, 香雞）"
              />
              <div class="purchase-params">
                <div class="purchase-param">
                  <span class="param-label">買過 ≥</span>
                  <input v-model.number="condition.value.minCount" type="number" min="1" class="form-control param-input" placeholder="1" />
                  <span class="param-label">次</span>
                </div>
                <div class="purchase-param">
                  <span class="param-label">近</span>
                  <input v-model.number="condition.value.withinDays" type="number" min="1" class="form-control param-input" placeholder="不限" />
                  <span class="param-label">天內</span>
                </div>
              </div>
              <small class="form-text">以訂單品項名稱模糊比對，任一關鍵字符合即算；適用外部匯入／POS 訂單。次數以已完成訂單數計</small>
            </div>

            <div v-else-if="periodTypes.includes(condition.conditionType)" class="purchase-condition">
              <div class="purchase-params">
                <div class="purchase-param">
                  <span class="param-label">近</span>
                  <input v-model.number="condition.value.withinDays" type="number" min="1" class="form-control param-input" placeholder="30" />
                  <span class="param-label">天內</span>
                </div>
                <div v-if="condition.operator === 'between'" class="purchase-param">
                  <input v-model.number="condition.value.min" type="number" min="0" class="form-control param-input" :placeholder="amountPlaceholder(condition)" />
                  <span class="param-label">～</span>
                  <input v-model.number="condition.value.max" type="number" min="0" class="form-control param-input" :placeholder="amountPlaceholder(condition)" />
                  <span class="param-label">{{ unitLabel(condition) }}</span>
                </div>
                <div v-else class="purchase-param">
                  <input v-model.number="condition.value.threshold" type="number" min="0" class="form-control param-input" :placeholder="amountPlaceholder(condition)" />
                  <span class="param-label">{{ unitLabel(condition) }}</span>
                </div>
              </div>
              <small class="form-text">
                {{ condition.conditionType === 'purchase_amount_within_days'
                  ? '加總近 N 天內已完成訂單的交易金額'
                  : '計算近 N 天內已完成訂單的交易筆數' }}
              </small>
            </div>

            <div v-else-if="condition.conditionType === 'registration_date'" class="purchase-condition">
              <div v-if="condition.operator === 'between'" class="date-inputs">
                <input v-model="condition.value.start" type="date" class="form-control" />
                <span>至</span>
                <input v-model="condition.value.end" type="date" class="form-control" />
              </div>
              <input v-else v-model="condition.value" type="date" class="form-control" />
            </div>

            <div v-else-if="plainNumericTypes.includes(condition.conditionType) && condition.operator === 'between'" class="purchase-param">
              <input v-model.number="condition.value.min" type="number" min="0" class="form-control param-input" placeholder="下限" />
              <span class="param-label">～</span>
              <input v-model.number="condition.value.max" type="number" min="0" class="form-control param-input" placeholder="上限" />
            </div>

            <input
              v-else-if="plainNumericTypes.includes(condition.conditionType)"
              v-model="condition.value"
              type="number"
              min="0"
              class="form-control"
              placeholder="請輸入條件值"
            />

            <input
              v-else
              v-model="condition.value"
              type="text"
              class="form-control"
              placeholder="請先選擇條件類型"
              disabled
            />
          </div>
          <button
            v-if="group.conditions.length > 1"
            type="button"
            class="btn btn-sm btn-danger"
            @click="removeCondition(groupIndex, conditionIndex)"
          >
            <i class="ri-close-line"></i>
          </button>
        </div>
      </div>

      <button type="button" class="btn btn-sm btn-secondary" @click="addCondition(groupIndex)">
        <i class="ri-add-line"></i>
        新增條件
      </button>
    </div>

    <button type="button" class="btn btn-secondary" @click="addConditionGroup">
      <i class="ri-add-line"></i>
      新增條件組
    </button>

    <ProductPickerModal
      :open="showProductPicker"
      title="選擇商品"
      :variant-required="false"
      @close="closeProductPicker"
      @select="handleProductSelect"
    />
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import TagSelector from '@/components/TagSelector.vue';
import ProductPickerModal from '@/components/ProductPickerModal.vue';
import { memberTierService } from '@/api/modules/memberTiers';
import { productService } from '@/api/modules/products';
import {
  PURCHASE_CONDITION_TYPES,
  PERIOD_PURCHASE_CONDITION_TYPES,
  PLAIN_NUMERIC_TYPES,
  CHOICE_TYPES,
  TAIWAN_CITIES,
  createEmptyCondition,
  createEmptyGroup,
  createPurchaseConditionValue,
  createPeriodPurchaseValue,
  validateConditionGroups,
  describeConditionGroups
} from '@/utils/memberConditions';

export default {
  name: 'MemberConditionGroups',
  components: { TagSelector, ProductPickerModal },
  props: {
    modelValue: {
      type: Array,
      required: true
    },
    mode: {
      type: String,
      default: 'auto-tag'
    }
  },
  emits: ['update:modelValue'],
  methods: {
    validate() {
      return validateConditionGroups(this.modelValue);
    },
    describe() {
      const tiers = Array.isArray(this.memberTiers) ? this.memberTiers : (this.memberTiers?.value || []);
      const tierLabels = Object.fromEntries(tiers.map(tier => [tier.value, tier.label]));
      return describeConditionGroups(this.modelValue, { member_tier: tierLabels });
    }
  },
  setup(props, { emit }) {
    const showLogicInfo = ref(false);
    const memberTiers = ref([]);
    const categoryOptions = ref([]);
    const showProductPicker = ref(false);
    const productPickerCondition = ref(null);
    const purchaseTypes = PURCHASE_CONDITION_TYPES;
    const periodTypes = PERIOD_PURCHASE_CONDITION_TYPES;
    const plainNumericTypes = PLAIN_NUMERIC_TYPES;
    const choiceTypes = CHOICE_TYPES;
    const cities = TAIWAN_CITIES;
    const genderOptions = [
      { value: 'male', label: '男' },
      { value: 'female', label: '女' },
      { value: 'neutral', label: '中性' },
      { value: 'unknown', label: '未設定' }
    ];
    const birthMonthOptions = Array.from({ length: 12 }, (_, index) => ({
      value: String(index + 1),
      label: `${index + 1} 月`
    }));

    const loadMemberTiers = async () => {
      try {
        const response = await memberTierService.getTiers({ isActive: true });
        memberTiers.value = (response.data || []).map(tier => ({
          value: tier.tierCode || String(tier.id),
          label: tier.tierName
        }));
      } catch (error) {
        memberTiers.value = [];
      }
    };

    const loadProductCategories = async () => {
      try {
        const response = await productService.getCategoriesFlat();
        const flat = response.data || [];
        const byParent = {};
        flat.forEach(cat => {
          const key = cat.parentId || 'root';
          (byParent[key] = byParent[key] || []).push(cat);
        });
        const ordered = [];
        const walk = (parentKey, depth) => {
          (byParent[parentKey] || []).forEach(cat => {
            ordered.push({ id: cat.id, label: `${'　'.repeat(depth)}${cat.name}` });
            walk(cat.id, depth + 1);
          });
        };
        walk('root', 0);
        categoryOptions.value = ordered;
      } catch (error) {
        categoryOptions.value = [];
      }
    };

    const sync = () => {
      emit('update:modelValue', props.modelValue);
    };

    const addConditionGroup = () => {
      props.modelValue.push(createEmptyGroup());
      sync();
    };

    const removeConditionGroup = (groupIndex) => {
      props.modelValue.splice(groupIndex, 1);
      sync();
    };

    const addCondition = (groupIndex) => {
      props.modelValue[groupIndex].conditions.push(createEmptyCondition());
      sync();
    };

    const removeCondition = (groupIndex, conditionIndex) => {
      props.modelValue[groupIndex].conditions.splice(conditionIndex, 1);
      sync();
    };

    const handleConditionTypeChange = (condition) => {
      condition.value = '';
      if (purchaseTypes.includes(condition.conditionType)) {
        condition.operator = 'greater_or_equal';
        condition.value = createPurchaseConditionValue(condition.conditionType);
      } else if (periodTypes.includes(condition.conditionType)) {
        condition.operator = 'greater_or_equal';
        condition.value = createPeriodPurchaseValue();
      } else if (plainNumericTypes.includes(condition.conditionType)) {
        condition.operator = 'greater_or_equal';
      } else if (choiceTypes.includes(condition.conditionType)) {
        condition.operator = 'equals';
      } else if (condition.conditionType === 'tag_exists') {
        condition.operator = 'exists';
      } else if (condition.conditionType === 'registration_date') {
        condition.operator = 'greater_or_equal';
      } else {
        condition.operator = '';
      }
    };

    const handleOperatorChange = (condition) => {
      if (plainNumericTypes.includes(condition.conditionType)) {
        if (condition.operator === 'between') {
          condition.value = { min: null, max: null };
        } else if (condition.value && typeof condition.value === 'object') {
          condition.value = '';
        }
      } else if (choiceTypes.includes(condition.conditionType)) {
        if (['in', 'not_in'].includes(condition.operator)) {
          condition.value = Array.isArray(condition.value)
            ? condition.value
            : (condition.value ? [condition.value] : []);
        } else if (Array.isArray(condition.value)) {
          condition.value = condition.value[0] || '';
        }
      } else if (condition.conditionType === 'registration_date') {
        if (condition.operator === 'between') {
          condition.value = { start: '', end: '' };
        } else if (condition.value && typeof condition.value === 'object') {
          condition.value = '';
        }
      } else if (periodTypes.includes(condition.conditionType) && condition.value && typeof condition.value === 'object') {
        if (condition.operator !== 'between') {
          condition.value.min = null;
          condition.value.max = null;
        }
      }
    };

    const isMultiChoice = (condition) => ['in', 'not_in'].includes(condition.operator);

    const isChoiceChecked = (condition, item) => Array.isArray(condition.value) && condition.value.includes(item);

    const toggleChoice = (condition, item) => {
      const current = Array.isArray(condition.value) ? [...condition.value] : [];
      const index = current.indexOf(item);
      if (index >= 0) current.splice(index, 1);
      else current.push(item);
      condition.value = current;
    };

    const amountPlaceholder = (condition) => (
      condition.conditionType === 'purchase_amount_within_days' ? '金額' : '次數'
    );

    const unitLabel = (condition) => (
      condition.conditionType === 'purchase_amount_within_days' ? '元' : '次'
    );

    const openProductPicker = (condition) => {
      productPickerCondition.value = condition;
      showProductPicker.value = true;
    };

    const closeProductPicker = () => {
      showProductPicker.value = false;
      productPickerCondition.value = null;
    };

    const handleProductSelect = ({ product }) => {
      const condition = productPickerCondition.value;
      if (condition && product) {
        condition.value.productId = product.id;
        condition.value.productName = product.name;
      }
      closeProductPicker();
    };

    onMounted(() => {
      loadMemberTiers();
      loadProductCategories();
    });

    return {
      showLogicInfo,
      memberTiers,
      categoryOptions,
      showProductPicker,
      purchaseTypes,
      periodTypes,
      plainNumericTypes,
      choiceTypes,
      cities,
      genderOptions,
      birthMonthOptions,
      addConditionGroup,
      removeConditionGroup,
      addCondition,
      removeCondition,
      handleConditionTypeChange,
      handleOperatorChange,
      isMultiChoice,
      isChoiceChecked,
      toggleChoice,
      amountPlaceholder,
      unitLabel,
      openProductPicker,
      closeProductPicker,
      handleProductSelect
    };
  }
};
</script>

<style scoped>
.btn {
  padding: 6px 16px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.btn-sm { padding: 4px 10px; }
.btn-secondary { background: #f3f4f6; color: #374151; border: 1px solid #e5e7eb; }
.btn-secondary:hover { background: #e5e7eb; }
.btn-danger { background: #fee2e2; color: #b91c1c; }

.form-group { margin-bottom: 16px; }
.form-control {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;
}
.form-text {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #909399;
}
.radio-group { display: flex; gap: 20px; flex-wrap: wrap; }
.radio-label, .checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}
.checkbox-group {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
}

.logic-info-box {
  margin-bottom: 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 8px;
  color: white;
  overflow: hidden;
}
.logic-info-box.collapsed {
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
  border: 2px solid #667eea;
}
.logic-info-box.collapsed .info-header { color: #667eea; }
.info-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
  user-select: none;
}
.info-header-left { display: flex; align-items: center; gap: 8px; }
.info-title { font-size: 15px; font-weight: 600; }
.info-content { padding: 0 16px 16px 16px; }
.info-list { margin: 0 0 12px 0; padding-left: 20px; }
.info-list li { margin-bottom: 8px; line-height: 1.6; }
.info-list ul { margin-top: 4px; padding-left: 20px; }
.info-example {
  font-size: 13px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 4px;
  margin: 0;
  line-height: 1.8;
}
.result-text { display: block; margin-top: 8px; font-weight: 600; }
.badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}
.badge-and { background: rgba(103, 194, 58, 0.9); color: white; }
.badge-or { background: rgba(245, 108, 108, 0.9); color: white; }
.slide-fade-enter-active, .slide-fade-leave-active { transition: all 0.3s ease; }
.slide-fade-enter-from, .slide-fade-leave-to { transform: translateY(-10px); opacity: 0; }

.condition-group {
  padding: 16px;
  margin-bottom: 16px;
  background-color: #f5f7fa;
  border-radius: 4px;
}
.group-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  font-weight: 600;
}
.condition-item { margin-bottom: 12px; }
.condition-row { display: flex; gap: 12px; align-items: flex-start; }
.flex-1 { flex: 1; }
.flex-2 { flex: 2; }
.purchase-condition { display: flex; flex-direction: column; gap: 8px; }
.purchase-params { display: flex; flex-wrap: wrap; gap: 12px; }
.purchase-param { display: flex; align-items: center; gap: 6px; }
.param-label { font-size: 13px; color: #606266; white-space: nowrap; }
.param-input { width: 90px; }
.product-pick-btn {
  justify-content: flex-start;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.date-inputs { display: flex; align-items: center; gap: 8px; }
.date-inputs .form-control { flex: 1; }

@media (max-width: 768px) {
  .condition-row { flex-direction: column; align-items: stretch; }
}
</style>
